import React, { useState, useEffect } from 'react';
import { Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import AuthImage, { openAuthFile } from '../../components/Common/AuthImage';
import { waybillsAPI } from '../../helpers/api';

interface WaybillPhoto {
  id: string;
  fileName: string;
  filePath: string;
  createdAt: string;
}

interface Waybill {
  id: string;
  waybillNumber: string;
  qrCode: string | null;
  status: string;
  supplierName: string | null;
  notes: string | null;
  quantity: number | null;
  unit: string | null;
  tonnage: number | null;
  createdAt: string;
  scannedAt: string | null;
  gibMatchedAt: string | null;
  site: { name: string; project: { name: string } } | null;
  scannedBy: { firstName: string; lastName: string } | null;
  createdBy: { firstName: string; lastName: string } | null;
  photos?: WaybillPhoto[];
}

interface WaybillDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  waybill: Waybill | null;
  onUpdate?: () => void;
}

const statusStyles: Record<string, string> = {
  SCANNED: 'bg-info bg-opacity-10 text-info',
  PENDING: 'bg-warning bg-opacity-10 text-warning',
  MATCHED: 'bg-success bg-opacity-10 text-success',
  NOT_ARRIVED: 'bg-danger bg-opacity-10 text-danger',
  MANUAL: 'bg-secondary bg-opacity-10 text-secondary',
};

const statusLabels: Record<string, string> = {
  SCANNED: 'Okutuldu',
  PENDING: 'Bekliyor',
  MATCHED: 'Eşleşti',
  NOT_ARRIVED: 'Gelmedi',
  MANUAL: 'Manuel',
};

const statusOptions = [
  { value: 'SCANNED', label: 'Okutuldu' },
  { value: 'PENDING', label: 'Bekliyor' },
  { value: 'MATCHED', label: 'Eşleşti' },
  { value: 'NOT_ARRIVED', label: 'Gelmedi' },
  { value: 'MANUAL', label: 'Manuel' },
];

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const WaybillDetailModal: React.FC<WaybillDetailModalProps> = ({
  isOpen,
  toggle,
  waybill,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    waybillNumber: '',
    supplierName: '',
    notes: '',
    status: '',
    quantity: '',
    unit: '',
    tonnage: '',
  });

  // Reset form when waybill changes
  useEffect(() => {
    if (waybill) {
      setFormData({
        waybillNumber: waybill.waybillNumber || '',
        supplierName: waybill.supplierName || '',
        notes: waybill.notes || '',
        status: waybill.status || '',
        quantity: waybill.quantity?.toString() || '',
        unit: waybill.unit || '',
        tonnage: waybill.tonnage?.toString() || '',
      });
      setIsEditing(false);
    }
  }, [waybill]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, status: e.target.value }));
  };

  const handleSave = async () => {
    if (!waybill) return;

    try {
      setSaving(true);
      await waybillsAPI.update(waybill.id, {
        waybillNumber: formData.waybillNumber,
        supplierName: formData.supplierName || null,
        notes: formData.notes || null,
        status: formData.status,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        unit: formData.unit || null,
        tonnage: formData.tonnage ? parseFloat(formData.tonnage) : null,
      });
      toast.success('İrsaliye güncellendi');
      setIsEditing(false);
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (waybill) {
      setFormData({
        waybillNumber: waybill.waybillNumber || '',
        supplierName: waybill.supplierName || '',
        notes: waybill.notes || '',
        status: waybill.status || '',
        quantity: waybill.quantity?.toString() || '',
        unit: waybill.unit || '',
        tonnage: waybill.tonnage?.toString() || '',
      });
    }
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!waybill) return;
    if (!window.confirm('Bu irsaliyeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await waybillsAPI.delete(waybill.id);
      toast.success('İrsaliye silindi');
      toggle();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  if (!waybill) return null;

  const headerActions = (
    <>
      {!isEditing ? (
        <>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={() => setIsEditing(true)}
            title="Düzenle"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleDelete}
            title="Sil"
            style={{ color: '#f06548' }}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleSave}
            disabled={saving}
            title="Kaydet"
            style={{ color: saving ? '#adb5bd' : '#0ab39c' }}
          >
            {saving ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
          </button>
          <button
            type="button"
            className="side-modal-expand-btn me-1"
            onClick={handleCancel}
            disabled={saving}
            title="İptal"
          >
            <i className="ri-close-line"></i>
          </button>
        </>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="İrsaliye Detayı"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-start justify-content-between">
            <div>
              {isEditing ? (
                <Input
                  type="text"
                  name="waybillNumber"
                  value={formData.waybillNumber}
                  onChange={handleInputChange}
                  className="form-control-lg"
                  style={{ fontWeight: 600 }}
                />
              ) : (
                <h4 className="mb-1" style={{ fontWeight: 600 }}>
                  {waybill.waybillNumber}
                </h4>
              )}
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                {waybill.site
                  ? `${waybill.site.project.name} / ${waybill.site.name}`
                  : 'Şantiye belirtilmedi'}
              </p>
            </div>
            {isEditing ? (
              <Input
                type="select"
                name="status"
                value={formData.status}
                onChange={handleSelectChange}
                bsSize="sm"
                style={{ width: 'auto' }}
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            ) : (
              <span
                className={`px-2 py-1 rounded-pill small ${statusStyles[waybill.status]}`}
                style={{ fontWeight: 500 }}
              >
                {statusLabels[waybill.status]}
              </span>
            )}
          </div>
        </div>

        {/* Details Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3 mb-3">
          <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Detaylar
          </div>
          <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
            {/* Tedarikçi */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Tedarikçi</span>
              {isEditing ? (
                <Input
                  type="text"
                  name="supplierName"
                  value={formData.supplierName}
                  onChange={handleInputChange}
                  placeholder="Tedarikçi adı"
                  bsSize="sm"
                  style={{ width: '200px', textAlign: 'right' }}
                />
              ) : (
                <span style={{ fontWeight: 500 }}>{waybill.supplierName || '-'}</span>
              )}
            </div>
            {/* Miktar */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Miktar</span>
              {isEditing ? (
                <div className="d-flex gap-1">
                  <Input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    placeholder="Adet"
                    bsSize="sm"
                    style={{ width: '80px', textAlign: 'right' }}
                    min={0}
                    step="0.01"
                  />
                  <Input
                    type="text"
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    placeholder="Birim"
                    bsSize="sm"
                    style={{ width: '70px' }}
                  />
                </div>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {waybill.quantity ? `${Number(waybill.quantity).toLocaleString('tr-TR')} ${waybill.unit || ''}`.trim() : '-'}
                </span>
              )}
            </div>
            {/* Tonaj */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Tonaj</span>
              {isEditing ? (
                <Input
                  type="number"
                  name="tonnage"
                  value={formData.tonnage}
                  onChange={handleInputChange}
                  placeholder="Ton"
                  bsSize="sm"
                  style={{ width: '100px', textAlign: 'right' }}
                  min={0}
                  step="0.001"
                />
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {waybill.tonnage ? `${Number(waybill.tonnage).toLocaleString('tr-TR')} ton` : '-'}
                </span>
              )}
            </div>
            {/* Okuyan */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Okuyan</span>
              <span style={{ fontWeight: 500 }}>
                {waybill.scannedBy
                  ? `${waybill.scannedBy.firstName} ${waybill.scannedBy.lastName}`
                  : '-'}
              </span>
            </div>
            {/* Okutma Tarihi */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Okutma Tarihi</span>
              <span>{formatDate(waybill.scannedAt)}</span>
            </div>
            {/* Kayıt Tarihi */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: waybill.gibMatchedAt ? '1px solid #e9ecef' : 'none' }}>
              <span className="text-muted">Kayıt Tarihi</span>
              <span>{formatDate(waybill.createdAt)}</span>
            </div>
            {/* Logo Eşleşme Tarihi */}
            {waybill.gibMatchedAt && (
              <div className="d-flex align-items-center justify-content-between px-3 py-2">
                <span className="text-muted">Logo Eşleşme</span>
                <span>{formatDate(waybill.gibMatchedAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* QR Code Card */}
        {waybill.qrCode && (
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3 mb-3">
            <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              QR Kod Bilgileri
            </div>
            <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
              {(() => {
                try {
                  const qrData = JSON.parse(waybill.qrCode);
                  const labels: Record<string, string> = {
                    vkntckn: 'Satıcı VKN/TCKN',
                    avkntckn: 'Alıcı VKN/TCKN',
                    senaryo: 'Senaryo',
                    tip: 'Tip',
                    tarih: 'Tarih',
                    no: 'Belge No',
                    ettn: 'ETTN',
                    sevktarihi: 'Sevk Tarihi',
                    sevkzamani: 'Sevk Zamanı',
                    tasiyicivkn: 'Taşıyıcı VKN',
                    plaka: 'Plaka',
                  };
                  const entries = Object.entries(qrData);
                  return entries.map(([key, value], idx) => (
                    <div
                      key={key}
                      className="d-flex align-items-center justify-content-between px-3 py-2"
                      style={{ borderBottom: idx < entries.length - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <span className="text-muted small">{labels[key] || key}</span>
                      <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{String(value)}</span>
                    </div>
                  ));
                } catch {
                  if (waybill.qrCode.includes('http')) {
                    return (
                      <div className="px-3 py-2">
                        <a
                          href={waybill.qrCode}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary small"
                          style={{ wordBreak: 'break-all' }}
                        >
                          {waybill.qrCode}
                        </a>
                      </div>
                    );
                  }
                  return (
                    <div className="px-3 py-2">
                      <code style={{ wordBreak: 'break-all', fontSize: '0.8rem' }}>
                        {waybill.qrCode}
                      </code>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        )}

        {/* Notes Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3 mb-3">
          <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Not
          </div>
          {isEditing ? (
            <Input
              type="textarea"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Not ekleyin..."
            />
          ) : (
            <p className="mb-0" style={{ color: waybill.notes ? '#495057' : '#adb5bd' }}>
              {waybill.notes || 'Not yok'}
            </p>
          )}
        </div>

        {/* Photos Card */}
        {waybill.photos && waybill.photos.length > 0 && (
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Fotoğraflar
              </span>
              <span className="text-muted small">{waybill.photos.length} adet</span>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {waybill.photos.map((photo) => (
                <div
                  key={photo.id}
                  className="rounded-3 overflow-hidden"
                  style={{
                    width: '100px',
                    height: '100px',
                    cursor: 'pointer',
                  }}
                  onClick={() => openAuthFile(photo.filePath)}
                >
                  <AuthImage
                    src={photo.filePath}
                    alt={photo.fileName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </SideModal>
  );
};

export default WaybillDetailModal;
